"use client"

import { useCallback, useState } from "react"
import { useRouter } from "next/navigation"
import { useRunContext } from "@/contexts/run-context"
import { createRun } from "@/lib/api/workflows"
import { Card } from "@/components/ui/card"
import { Code2, Database } from "lucide-react"

/**
 * Home Page - Mode selection for new runs
 * 
 * Shows two cards for selecting Codebase or Database migration mode.
 * Creates a new run with the selected mode and navigates to it.
 */
export default function Home() {
  const router = useRouter()
  const { setRunId, setMode } = useRunContext()
  const [isCreating, setIsCreating] = useState(false)

  const handleSelectMode = useCallback(async (mode: 'codebase' | 'database') => {
    if (isCreating) return

    try {
      setIsCreating(true)
      const config = mode === 'database'
        ? { source_db_type: 'postgresql', source_db_connection: 'pending' }
        : { repo_url: '' }
      const response = await createRun({ mode, config })
      // Set mode BEFORE runId so that when RunLayout mounts and reads
      // contextMode, it already has the correct value. In React 18+ these
      // are separate renders (after an await), so order matters.
      const workflowId = response.run_id ?? ''
      if (!workflowId) {
        throw new Error('Backend did not return a workflow id')
      }
      setMode(mode)
      setRunId(workflowId)
      router.push(`/workflow/${workflowId}`)
    } catch (error) {
      console.error('[Home] Failed to create new run:', error)
    } finally {
      setIsCreating(false)
    }
  }, [isCreating, setRunId, setMode, router])

  return (
    <div className="h-screen flex items-center justify-center p-4">
      <div className="flex gap-6">
        {/* Database Card */}
        <button
          onClick={() => handleSelectMode('database')}
          disabled={isCreating}
          className="group"
        >
          <Card className="w-40 h-40 flex flex-col items-center justify-center gap-3 bg-card/95 backdrop-blur-xl border-border hover:border-primary/50 hover:shadow-lg hover:shadow-primary/10 transition-all cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed">
            <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
              <Database className="w-6 h-6 text-primary" />
            </div>
            <div className="text-center">
              <div className="font-medium text-md">Data Source</div>
              <div className="text-xs text-muted-foreground">Identity ETL Process



              </div>
            </div>
          </Card>
        </button>

        {/* Codebase Card */}
        <button
          onClick={() => handleSelectMode('codebase')}
          disabled={isCreating}
          className="group"
        >
          <Card className="w-40 h-40 flex flex-col items-center justify-center gap-3 bg-card/95 backdrop-blur-xl border-border hover:border-primary/50 hover:shadow-lg hover:shadow-primary/10 transition-all cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed">
            <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
              <Code2 className="w-6 h-6 text-primary" />
            </div>
            <div className="text-center">
              <div className="font-medium text-md">Source Code</div>
              <div className="text-xs text-muted-foreground">Application → IDP</div>
            </div>
          </Card>
        </button>

      </div>
    </div>
  )
}
